import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  User,
  FileText,
  Building,
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  Loader2,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Calendar,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import PhoneVerificationModal from './PhoneVerificationModal';

const token = 'cookie-auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Types de permis
const PERMIT_TYPES = [
  { value: 'A', label: 'Permis A (Moto)' },
  { value: 'B', label: 'Permis B (Véhicule léger)' },
  { value: 'C', label: 'Permis C (Poids lourd)' },
  { value: 'D', label: 'Permis D (Transport en commun)' },
  { value: 'E', label: 'Permis E (Remorque)' },
];

// Secteurs d'activité
const BUSINESS_SECTORS = [
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'agroalimentaire', label: 'Agroalimentaire' },
  { value: 'btp_construction', label: 'BTP / Construction' },
  { value: 'commerce_distribution', label: 'Commerce / Distribution' },
  { value: 'energie_mines', label: 'Énergie / Mines' },
  { value: 'industrie_manufacture', label: 'Industrie / Manufacture' },
  { value: 'logistique_transport', label: 'Logistique / Transport' },
  { value: 'sante_pharmaceutique', label: 'Santé / Pharmaceutique' },
  { value: 'services_conseil', label: 'Services / Conseil' },
  { value: 'technologies_telecoms', label: 'Technologies / Télécoms' },
  { value: 'textile_mode', label: 'Textile / Mode' },
  { value: 'autre', label: 'Autre' }
];

/**
 * ProfileCompletionWizard - Modal wizard pour compléter le profil
 */
const ProfileCompletionWizard = ({ open, onClose, onComplete }) => {
  const { user, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [formData, setFormData] = useState({
    // Infos personnelles
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    // Documents chauffeur
    cniNumber: '',
    permitType: '',
    permitNumber: '',
    permitExpirationDate: '',
    // Documents entreprise
    companyName: '',
    sector: '',
    sectorOther: '',
    rccm: '',
    dfe: '',
  });

  // Initialiser avec les données existantes
  useEffect(() => {
    if (user && open) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        address: user.address || '',
        cniNumber: user.cniNumber || '',
        permitType: user.permitType || '',
        permitNumber: user.permitNumber || '',
        permitExpirationDate: user.permitExpirationDate?.split('T')[0] || '',
        companyName: user.companyName || '',
        sector: user.sector || '',
        sectorOther: user.sectorOther || '',
        rccm: user.rccm || '',
        dfe: user.dfe || '',
      });
      setCurrentStep(0);
      // Vérifier si le téléphone est déjà vérifié
      setPhoneVerified(user.phoneVerified || user.notificationPhoneVerified || false);
      // Fetch verification status
      fetchPhoneVerificationStatus();
    }
  }, [user, open]);

  const fetchPhoneVerificationStatus = async () => {
    try {

      if (!token) return;

      const response = await axios.get(`${API}/phone-verification/status`, {});
      
      setPhoneVerified(response.data.is_verified);
    } catch (error) {
      console.error('Error fetching phone verification status:', error);
    }
  };

  // Définir les étapes selon le rôle
  const getSteps = () => {
    const role = user?.role || '';
    const isCompany = user?.accountType === 'entreprise' || user?.companyName;
    const hasPhone = user?.phone || formData.phone;
    
    const steps = [
      {
        id: 'personal',
        title: 'Informations personnelles',
        description: 'Vos coordonnées de base',
        icon: User,
        fields: ['firstName', 'lastName', 'phone', 'address']
      }
    ];

    // Ajouter étape vérification téléphone si un numéro existe
    if (hasPhone && !phoneVerified) {
      steps.push({
        id: 'phone_verification',
        title: 'Vérification du téléphone',
        description: 'Confirmez votre numéro',
        icon: ShieldCheck,
        fields: []
      });
    }

    // Ajouter étape documents chauffeur
    if (role === 'chauffeur' || role === 'driver') {
      steps.push({
        id: 'driver_docs',
        title: 'Documents chauffeur',
        description: 'CNI et permis de conduire',
        icon: FileText,
        fields: ['cniNumber', 'permitType', 'permitNumber', 'permitExpirationDate']
      });
    }

    // Ajouter étape entreprise si applicable
    if (isCompany || role === 'client' || role === 'expediteur') {
      steps.push({
        id: 'company',
        title: 'Informations entreprise',
        description: 'Documents légaux et secteur d\'activité',
        icon: Building,
        fields: ['companyName', 'sector', 'sectorOther', 'rccm', 'dfe']
      });
    }

    return steps;
  };

  const steps = getSteps();
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateCurrentStep = () => {
    const currentStepData = steps[currentStep];
    
    // Validation basique - au moins un champ rempli par étape
    if (currentStep === 0) {
      if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
        toast({
          title: 'Champs requis',
          description: 'Le nom et prénom sont obligatoires',
          variant: 'destructive'
        });
        return false;
      }
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Préparer les données à envoyer
      const updateData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim && value.trim() !== '') {
          updateData[key] = value.trim();
        } else if (value && typeof value !== 'string') {
          updateData[key] = value;
        }
      });

      // Mettre à jour le profil
      await axios.put(
        `${API}/auth/profile`,
        updateData,
        {}
      );

      toast({
        title: '✅ Profil mis à jour',
        description: 'Vos informations ont été enregistrées avec succès'
      });

      // Rafraîchir les données utilisateur
      if (refreshUser) {
        await refreshUser();
      }

      onComplete?.();
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de mettre à jour le profil',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];
    
    switch (step.id) {
      case 'personal':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="firstName"
                    placeholder="Jean"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="lastName"
                    placeholder="Kouassi"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+225 07 XX XX XX XX"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="address"
                  placeholder="Abidjan, Cocody..."
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        );

      case 'phone_verification':
        return (
          <div className="space-y-4">
            <div className="text-center py-6">
              {phoneVerified ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-700">Numéro vérifié</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Votre numéro {formData.phone || user?.phone} est vérifié.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                    <ShieldCheck className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Vérifiez votre numéro</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Pour recevoir les notifications, veuillez vérifier votre numéro de téléphone.
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">
                      {formData.phone || user?.phone}
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowPhoneVerification(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Vérifier mon numéro
                  </Button>
                </div>
              )}
            </div>
          </div>
        );

      case 'driver_docs':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cniNumber">Numéro CNI</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="cniNumber"
                  placeholder="CI-XXXXXXXXXX"
                  value={formData.cniNumber}
                  onChange={(e) => handleInputChange('cniNumber', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="permitType">Type de permis</Label>
                <Select 
                  value={formData.permitType} 
                  onValueChange={(value) => handleInputChange('permitType', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PERMIT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="permitNumber">Numéro de permis</Label>
                <Input
                  id="permitNumber"
                  placeholder="XXXXXXXX"
                  value={formData.permitNumber}
                  onChange={(e) => handleInputChange('permitNumber', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="permitExpirationDate">Date d'expiration du permis</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="permitExpirationDate"
                  type="date"
                  value={formData.permitExpirationDate}
                  onChange={(e) => handleInputChange('permitExpirationDate', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        );

      case 'company':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Nom de l'entreprise</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="companyName"
                  placeholder="Ma Société SARL"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sector">Secteur d'activité</Label>
              <Select
                value={formData.sector}
                onValueChange={(value) => {
                  handleInputChange('sector', value);
                  if (value !== 'autre') handleInputChange('sectorOther', '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez votre secteur" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_SECTORS.map(sector => (
                    <SelectItem key={sector.value} value={sector.value}>
                      {sector.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.sector === 'autre' && (
              <div className="space-y-2">
                <Label htmlFor="sectorOther">Précisez votre secteur</Label>
                <Input
                  id="sectorOther"
                  placeholder="Ex: Consulting, Import-Export..."
                  value={formData.sectorOther}
                  onChange={(e) => handleInputChange('sectorOther', e.target.value)}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="rccm">RCCM (Registre du Commerce) <span className="text-gray-400 font-normal">— facultatif</span></Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="rccm"
                  placeholder="CI-ABJ-XXXX-X-XXXXX"
                  value={formData.rccm}
                  onChange={(e) => handleInputChange('rccm', e.target.value)}
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-gray-500">
                Numéro d'immatriculation au registre du commerce
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dfe">DFE (Déclaration Fiscale d'Existence) <span className="text-gray-400 font-normal">— facultatif</span></Label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="dfe"
                  placeholder="XXXXXXXXXX"
                  value={formData.dfe}
                  onChange={(e) => handleInputChange('dfe', e.target.value)}
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-gray-500">
                Numéro d'identification fiscale
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const CurrentStepIcon = steps[currentStep]?.icon;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {CurrentStepIcon && (
              <CurrentStepIcon className="w-5 h-5 text-emerald-600" />
            )}
            {steps[currentStep]?.title}
          </DialogTitle>
          <DialogDescription>
            {steps[currentStep]?.description}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Étape {currentStep + 1} sur {totalSteps}</span>
            <span className="font-medium text-emerald-600">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-center gap-2 pt-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                  index < currentStep
                    ? 'bg-emerald-600 text-white'
                    : index === currentStep
                    ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-600'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {index < currentStep ? (
                  <Check className="w-4 h-4" />
                ) : (
                  index + 1
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="py-4">
          {renderStepContent()}
        </div>

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0 || loading}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Plus tard
            </Button>
            <Button
              onClick={handleNext}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {currentStep === totalSteps - 1 ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Terminer
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {/* Phone Verification Modal - réutilisation du même composant */}
      <PhoneVerificationModal
        open={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        onVerified={() => {
          setPhoneVerified(true);
          setShowPhoneVerification(false);
          toast.success('Numéro vérifié avec succès !');
        }}
        phone={formData.phone || user?.phone}
        allowPhoneEdit={false}
      />
    </Dialog>
  );
};

export default ProfileCompletionWizard;
